import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, ExternalLink, MapPin, Lock } from "lucide-react";
import type { Event } from "@satyrsmc/shared/client";
import { formatDate } from "@/lib/date-utils";
import { EventStatus } from "@satyrsmc/shared/lib/enums";
import { SafeHtml } from "@satyrsmc/shared/components";
import { resolveEventLocationDisplay, resolveEventUrlDisplay } from "@/lib/event-location";

interface EventDetailsCardProps {
  event: Event;
  budgetName?: string;
  scenarioName?: string;
}

export function EventDetailsCard({ event, budgetName, scenarioName }: EventDetailsCardProps) {
  const locationDisplay = resolveEventLocationDisplay(
    event.event_location,
    event.event_location_link,
  );
  const urlDisplay = resolveEventUrlDisplay(event.event_url, event.event_url_text);

  return (
    <Card id="event-details" className="scroll-mt-28">
      <CardHeader>
        <CardTitle className="text-lg">Event Details</CardTitle>
        <CardDescription>
          Set the event type, dates, location, description, and ticket pricing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {event.status === EventStatus.Draft && <Badge variant="secondary">Draft</Badge>}
          {event.status === EventStatus.Cancelled && <Badge variant="destructive">Cancelled</Badge>}
          {event.members_only && (
            <Badge variant="secondary" className="gap-1">
              <Lock className="size-3" />
              Members Only
            </Badge>
          )}
        </div>
        {event.start_date && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4 text-muted-foreground" />
            <span>
              {formatDate(event.start_date!)}
              {event.end_date && ` – ${formatDate(event.end_date)}`}
            </span>
          </div>
        )}
        {urlDisplay && (
          <div>
            <a
              href={urlDisplay.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {urlDisplay.label}
              <ExternalLink className="size-3.5" />
            </a>
          </div>
        )}
        {locationDisplay && (
          <div className="flex items-start gap-2">
            <MapPin className="size-4 text-muted-foreground mt-0.5 shrink-0" />
            {locationDisplay.kind === "text" ? (
              <div className="space-y-0.5">
                <span className="text-sm">{locationDisplay.text}</span>
                {locationDisplay.linkUrl && (
                  <div>
                    <a
                      href={locationDisplay.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs hover:underline"
                    >
                      View on Map
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <a
                href={locationDisplay.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {locationDisplay.label}
              </a>
            )}
          </div>
        )}
        {(event.ga_ticket_cost != null ||
          event.day_pass_cost != null ||
          event.member_cost != null) && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            {event.ga_ticket_cost != null && (
              <div>
                <span className="text-muted-foreground">GA Ticket:</span> $
                {event.ga_ticket_cost.toLocaleString()}
              </div>
            )}
            {event.day_pass_cost != null && (
              <div>
                <span className="text-muted-foreground">Day Pass:</span> $
                {event.day_pass_cost.toLocaleString()}
              </div>
            )}
            {event.member_cost != null && (
              <div>
                <span className="text-muted-foreground">Member Cost:</span> $
                {event.member_cost.toLocaleString()}
              </div>
            )}
          </div>
        )}
        {event.refund_cutoff_date && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Refund cutoff:</span>
            <span>{formatDate(event.refund_cutoff_date)}</span>
          </div>
        )}
        {(event.ga_tickets_sold != null || event.day_passes_sold != null) && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            {event.ga_tickets_sold != null && (
              <div>
                <span className="text-muted-foreground">GA Sold:</span> {event.ga_tickets_sold}
              </div>
            )}
            {event.day_passes_sold != null && (
              <div>
                <span className="text-muted-foreground">Day Passes Sold:</span>{" "}
                {event.day_passes_sold}
              </div>
            )}
          </div>
        )}
        {(event.budget_id || event.scenario_id) && (
          <div className="space-y-2 text-sm">
            {event.budget_id && (
              <div>
                <span className="text-muted-foreground">Budget:</span>{" "}
                {budgetName ?? event.budget_id}
              </div>
            )}
            {event.scenario_id && (
              <div>
                <span className="text-muted-foreground">Scenario:</span>{" "}
                {scenarioName ?? event.scenario_id}
              </div>
            )}
          </div>
        )}
        {(event.start_location ||
          event.end_location ||
          event.facebook_event_url ||
          event.ride_cost != null) && (
          <div className="space-y-2 border-t pt-4 text-sm">
            <h4 className="font-medium text-muted-foreground">Ride Details</h4>
            <dl className="space-y-1">
              {event.start_location && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-24 shrink-0">Start</dt>
                  <dd>{event.start_location}</dd>
                </div>
              )}
              {event.end_location && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-24 shrink-0">End</dt>
                  <dd>{event.end_location}</dd>
                </div>
              )}
              {event.ride_cost != null && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-24 shrink-0">Ride cost</dt>
                  <dd>${event.ride_cost.toLocaleString()}</dd>
                </div>
              )}
              {event.facebook_event_url && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-24 shrink-0">Facebook</dt>
                  <dd>
                    <a
                      href={event.facebook_event_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline inline-flex items-center gap-1"
                    >
                      View event
                      <ExternalLink className="size-3" />
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
        {event.poster_url && (
          <div className="space-y-2 border-t pt-4">
            <h4 className="text-sm font-medium text-muted-foreground">Poster</h4>
            <img src={event.poster_url} alt="Event poster" className="max-w-xs rounded-md border" />
          </div>
        )}
        {event.description && (
          <div className="space-y-2 border-t pt-4">
            <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
            <SafeHtml
              html={event.description}
              className="prose prose-sm max-w-none text-foreground [&_strong]:font-semibold [&_a]:text-[var(--color-link)] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_p]:mb-3 [&_p:last-child]:mb-0"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
